import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Sparkles } from "lucide-react";
import { submitBetaSignup } from "../lib/api";
import { track } from "../lib/analytics";

const HEARD_OPTIONS = [
  "A friend or family member",
  "Social media",
  "Search engine",
  "An online community",
  "A podcast or article",
  "Other",
];

export const BetaForm = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    device: "iPhone",
    heardFrom: "",
    foundingMember: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onFounding = () => setForm((f) => ({ ...f, foundingMember: true }));
    window.addEventListener("heard:founding", onFounding);
    return () => window.removeEventListener("heard:founding", onFounding);
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) return toast.error("Please add your first name.");
    if (!form.email.trim()) return toast.error("Please add your email so we can reach you.");
    setLoading(true);
    try {
      await submitBetaSignup({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        device: form.device,
        heard_from: form.heardFrom,
        founding_member: form.foundingMember,
      });
      setDone(true);
      window.dispatchEvent(new CustomEvent("heard:signup"));
      toast.success("You're on the list — welcome to HEARD. 🌿");
      track("early_access_signup", "home-beta-form");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      if (status === 409) { setDone(true); window.dispatchEvent(new CustomEvent("heard:signup")); }
      toast(status === 409 ? msg : `⚠️ ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "block text-sm font-medium text-ink/80 mb-2";
  const inputCls =
    "w-full rounded-2xl border border-line bg-white px-4 py-3.5 text-base text-ink placeholder:text-ink/40 outline-none transition-colors duration-300 focus:border-sage focus:ring-2 focus:ring-sage/20";
  const submitLabel = form.foundingMember ? "Become a Founding Member" : "Reserve My Spot";

  if (done) {
    return (
      <div className="rounded-3xl bg-white border border-line p-8 sm:p-10 text-center shadow-[0_8px_30px_rgba(47,75,57,0.06)]" data-testid="beta-success">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-sage/15 text-sage-deep">
          <Check size={28} />
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-sage/10 px-4 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-sage-deep">
          <Sparkles size={13} className="text-sage" /> Founding Community
        </span>
        <h3 className="mt-5 font-serif text-3xl font-semibold text-heading">Thank you for joining the HEARD Founding Circle.</h3>
        <p className="mt-4 text-base leading-relaxed text-ink/75">
          Your spot has been reserved. We've sent a confirmation to your inbox.
        </p>
        <div className="mx-auto mt-6 max-w-sm rounded-2xl bg-cream-deep/60 px-5 py-4 text-left">
          <p className="text-[0.7rem] uppercase tracking-[0.2em] text-taupe">What happens next</p>
          <p className="mt-2 text-sm leading-relaxed text-ink/75">
            Invitations go out <span className="font-medium text-sage-deep">in phases</span> as beta testing expands —
            we'll reach out as we open more spots, with opportunities to help shape the future of HEARD.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-line p-6 sm:p-8 shadow-[0_8px_30px_rgba(47,75,57,0.06)]" data-testid="beta-form">
      {form.foundingMember && (
        <div className="mb-5 flex items-center gap-2 rounded-2xl bg-sage/10 px-4 py-3 text-sm text-sage-deep" data-testid="founding-badge">
          <Sparkles size={16} className="text-sage" /> Joining as a Founding Member
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bf-first" className={labelCls}>First name</label>
          <input id="bf-first" name="firstName" value={form.firstName} onChange={onChange} placeholder="First name" className={inputCls} data-testid="beta-first-name-input" />
        </div>
        <div>
          <label htmlFor="bf-last" className={labelCls}>Last name <span className="text-ink/40">(optional)</span></label>
          <input id="bf-last" name="lastName" value={form.lastName} onChange={onChange} placeholder="Last name" className={inputCls} data-testid="beta-last-name-input" />
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="bf-email" className={labelCls}>Email address</label>
        <input id="bf-email" name="email" type="email" required value={form.email} onChange={onChange} placeholder="you@example.com" className={inputCls} data-testid="beta-email-input" />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="bf-device" className={labelCls}>Mobile OS</label>
          <select id="bf-device" name="device" value={form.device} onChange={onChange} className={inputCls} data-testid="beta-device-select">
            <option value="iPhone">iPhone</option>
            <option value="Android">Android</option>
          </select>
        </div>
        <div>
          <label htmlFor="bf-heard" className={labelCls}>How did you hear about HEARD? <span className="text-ink/40">(optional)</span></label>
          <select id="bf-heard" name="heardFrom" value={form.heardFrom} onChange={onChange} className={inputCls} data-testid="beta-heard-from-select">
            <option value="">Select an option</option>
            {HEARD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="beta-founding-toggle">
        <input type="checkbox" name="foundingMember" checked={form.foundingMember} onChange={onChange} className="mt-0.5 h-4 w-4 rounded border-line text-sage focus:ring-sage/30" />
        <span className="text-sm text-ink/70 leading-relaxed">I'd like to join as a <span className="text-sage-deep font-medium">Founding Member</span> — early access & a voice in shaping HEARD.</span>
      </label>

      <button
        type="submit"
        disabled={loading}
        data-testid="beta-submit-button"
        className="mt-6 w-full rounded-full bg-sage px-6 py-3.5 text-sm font-medium text-white shadow-[0_6px_20px_rgba(126,154,107,0.35)] transition-all duration-300 hover:bg-sage-hover hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
      >
        {loading ? <><Loader2 size={16} className="animate-spin" /> Joining…</> : submitLabel}
      </button>
      <p className="mt-4 text-center text-xs leading-relaxed text-ink/50">No spam. Anonymous by design. We'll never sell your information.</p>
    </form>
  );
};
